/* eslint-disable no-nested-ternary */

import { loadStripe } from '@stripe/stripe-js';
import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { config } from '../../../modules/config';
import { GuestPortalContext } from '../../../modules/context/guestPortalContextV2';
import {
  type ErrorEnvelop,
  useGuestAuth,
  useManualQueryRequest,
  usePropertyConfig,
} from '../../../modules/hooks';
import type {
  CardInfo,
  DynamicQuestionPayload,
  Guests,
  GuestsRequest,
  MewsReservation,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import {
  useAddPackagesToGuest,
  useGetCardInfo,
  useGetDynamicQuestions,
  useGetReservation,
  useGetReservationRating,
  useGuestReservation,
} from '../../GuestPagesV2/endpoints/GuestPageEnpoints';
import { GuestPortalErrorPage } from '../../GuestPagesV2/GuestPortalErrorPage';
import { GuestPortalErrors } from '../../Modals/GuestPagesModalsV2/constants/GuestPortalConstants';

export const GuestPortalProvider = ({
  children,
  reservationId,
}: {
  children: React.ReactNode;
  reservationId: string;
}) => {
  const [reservation, setReservation] = useState<MewsReservation>();
  const [preregisteredGuest, setPreregisteredGuest] = useState<GuestsRequest>();
  const [guestData, setGuestData] = useState<Guests>();
  const [dynamicQuestionsData, setDynamicQuestionsData] = useState<
    DynamicQuestionPayload[]
  >([]);
  const [cardInfo, setCardInfo] = useState<CardInfo>();
  const [guestName, setGuestName] = useState<string>('');
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [stayEnded, setStayEnded] = useState<boolean>(false);

  const { token, generateAuthToken, loading, error } = useGuestAuth()!;

  const [{ data: reservationResult, loading: loadingReservation }, getMewsReservations] =
    useGetReservation(reservationId || '');

  const [{ data: cardResult, loading: cardInfoLoading }, getCardInfo] = useGetCardInfo();

  const [reservationRating, setReservationRating] = useState(0);

  const [{ data, loading: loadingReservationRating }, getReservationRating] =
    useGetReservationRating(reservationId || '');

  const {
    data: dynamicQuestionResult,
    loading: loadingDynamicQuestion,
    error: errorDynamicQuestion,
  } = useGetDynamicQuestions(reservation?.Property?.uuid || '', reservation?.Id || '');

  const [
    { data: addPackageResult, loading: loadingAddPackage, error: addPackageError },
    addPackageToGuest,
  ] = useAddPackagesToGuest(reservation?.Property?.uuid || '');

  // Fetch property config to get property-specific Stripe public key
  const propertyId = reservation?.Property?.uuid;
  const { data: propertyConfig } = usePropertyConfig(propertyId, {
    authGuestToken: token || undefined,
    enabled: !!propertyId && !!token,
  });

  // Determine which Stripe public key to use
  // Priority: 1) Property config, 2) Environment variable fallback
  const stripePublicKey = useMemo(() => {
    return propertyConfig?.stripe?.publicKey || config.stripe.publicKey;
  }, [propertyConfig?.stripe?.publicKey]);

  // Create Stripe promise with the appropriate public key
  const stripePromise = useMemo(() => {
    if (!stripePublicKey) {
      return Promise.resolve(null);
    }
    return loadStripe(stripePublicKey);
  }, [stripePublicKey]);

  useEffect(() => {
    if (!data) return;

    setReservationRating(data?.result?.rating);
  }, [data, loadingReservationRating]);

  const [
    { data: reservationGuest, loading: loadingPreregisterd },
    getGuestPreregistered,
  ] = useGuestReservation(reservationId || '');

  const [{ data: guestResult, loading: loadingGuestResult }, getGuest] =
    useManualQueryRequest<Guests>(
      endpoints.GUESTS,

      {
        authGuestToken: token,
        params: {
          email: guestEmail,
        },
      },

      { manual: true },
    );

  useEffect(() => {
    if (!error && !addPackageError) return;

    if (
      getApiErrorMessage(
        (error as AxiosError<ErrorEnvelop>) ||
          (addPackageError as AxiosError<ErrorEnvelop>),
      ) === GuestPortalErrors.JWT_EXPIRED
    ) {
      generateAuthToken({ reservationId });
    }
  }, [error, loading, reservationId, addPackageError]);

  useEffect(() => {
    if (!reservationResult) return;

    setGuestEmail(reservationResult?.result?.Reservations[0].Customer?.Email || '');
    setReservation(reservationResult?.result.Reservations[0]);
  }, [reservationResult]);

  useEffect(() => {
    if (!guestEmail) return;

    getGuest();
  }, [guestEmail]);

  useEffect(() => {
    if (!guestResult) return;

    setGuestData(guestResult?.result);
  }, [guestResult]);

  useEffect(() => {
    if (!reservationGuest) return;

    setPreregisteredGuest(reservationGuest?.result);

    if (reservationGuest?.result?.stripeId) {
      const customerId = reservationGuest.result.stripeId;
      getCardInfo({ customerId });
    }
  }, [reservationGuest]);

  useEffect(() => {
    if (!cardResult) return;

    setCardInfo(cardResult.result);
  }, [cardResult]);

  useEffect(() => {
    if (!token) return;

    getMewsReservations({ extended: true });
    getReservationRating();
  }, [token]);

  useEffect(() => {
    if (dynamicQuestionResult) {
      setDynamicQuestionsData(dynamicQuestionResult.result);
    }
  }, [dynamicQuestionResult]);

  const guestPortalContext = useMemo(() => {
    return {
      guestData,
      getGuest,
      reservation,
      getMewsReservations,
      loadingReservation,
      loading,
      error,
      preregisteredGuest,
      getGuestPreregistered,
      guestName,
      setGuestName,
      addPackageResult,
      addPackageToGuest,
      loadingAddPackage,
      addPackageError,
      cardInfo,
      cardInfoLoading,
      getCardInfo,
      dynamicQuestionsData,
      reservationRating,
      loadingReservationRating,
      stripePromise,
    };
  }, [
    guestData,
    getGuest,
    reservation,
    getMewsReservations,
    loadingReservation,
    loading,
    error,
    preregisteredGuest,
    getGuestPreregistered,
    guestName,
    setGuestName,
    addPackageResult,

    addPackageToGuest,
    loadingAddPackage,
    addPackageError,
    cardInfo,
    cardInfoLoading,
    getCardInfo,
    dynamicQuestionsData,
    reservationRating,
    loadingReservationRating,
    stripePromise,
  ]);

  useEffect(() => {
    if (!reservation) return;

    if (!reservation?.InternalReservation?.isVerified && reservation?.EndUtc) {
      const now = DateTime.now();
      const reservationEnd = DateTime.fromISO(reservation?.EndUtc || '');

      if (now > reservationEnd || reservation.State === 'Processed') {
        setStayEnded(true);
      }
    }
  }, [reservation]);

  useEffect(() => {
    if (loading || loadingReservation || loadingPreregisterd || loadingAddPackage) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
    }
  }, [loading || loadingReservation || loadingPreregisterd || loadingAddPackage]);

  return (
    <LoadingOverlay
      active={loading || loadingReservation || loadingPreregisterd || loadingAddPackage}
      text="Wait while we get things ready"
    >
      <GuestPortalContext.Provider value={guestPortalContext}>
        {error &&
        getApiErrorMessage(error as AxiosError<ErrorEnvelop>) !==
          GuestPortalErrors.JWT_EXPIRED ? (
          <GuestPortalErrorPage error={error as AxiosError<ErrorEnvelop>} />
        ) : stayEnded ? (
          <GuestPortalErrorPage unregisteredReservation />
        ) : (
          children
        )}
      </GuestPortalContext.Provider>
    </LoadingOverlay>
  );
};
